import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, RefreshCw, Box, PlusCircle, Eye, EyeOff, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const ProductPricing = ({ activeLead, isLightMode, draftProducts, setDraftProducts, onCreateLead }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Modal State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customPrice, setCustomPrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [showDraftReview, setShowDraftReview] = useState(false);
  const [draftLeadDetails, setDraftLeadDetails] = useState({ name: "", company: "", email: "" });

  const [revealedCosts, setRevealedCosts] = useState({});

  const toggleCost = (sku) => {
    setRevealedCosts(prev => ({
        ...prev,
        [sku]: !prev[sku]
    }));
  };
  useEffect(() => {
    // Initial load
    fetchProducts();
  }, []);

  const fetchProducts = async (term = "") => {
    setLoading(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("trade_token");
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products${term ? `?search=${term}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("trade_token");
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/products/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Sync Complete", { description: response.data.message });
      fetchProducts(); // Refresh list
    } catch (error) {
      console.error(error);
      toast.error("Sync Failed", { description: "Could not update from source sheet." });
    } finally {
      setSyncing(false);
    }
  };

  const openAddDialog = (product) => {
      setSelectedProduct(product);
      setCustomPrice(product.price_500?.replace('£', '') || "");
      setQuantity("1");
      setIsDialogOpen(true);
  };

  const handleDraftSubmit = () => {
      // Switch to Lead Form workflow
      onCreateLead(); // This now calls handleProceedToLeadForm from parent
  };

  const handleConfirmAdd = async () => {
      // 1. DRAFT MODE
      if (!activeLead) {
          const newItem = {
              sku: selectedProduct.sku,
              title: selectedProduct.title,
              price_point: selectedProduct.price_500, // Default ref
              special_price: customPrice,
              quantity: parseInt(quantity) || 1
          };
          setDraftProducts(prev => [...prev, newItem]);
          toast.success("Added to Quote Draft", { description: `${newItem.sku} (x${newItem.quantity})` });
          setIsDialogOpen(false);
          return;
      }

      // 2. ACTIVE LEAD MODE
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("trade_token");

      try {
          await axios.put(`${BACKEND_URL}/api/leads/${activeLead.id}/interest`, {
              sku: selectedProduct.sku,
              title: selectedProduct.title,
              price_point: selectedProduct.price_500, // Keep reference price
              special_price: customPrice,
              quantity: parseInt(quantity) || 1
          }, {
              headers: { Authorization: `Bearer ${token}` }
          });
          
          toast.success("Interest Added", { 
              description: `${selectedProduct.sku} added to ${activeLead.company} (Qty: ${quantity}, Price: £${customPrice})` 
          });
          setIsDialogOpen(false);
      } catch (error) {
          console.error(error);
          toast.error("Action Failed");
      }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts(searchTerm);
  };

  const containerClass = `rounded-sm border overflow-hidden ${isLightMode ? 'bg-white border-gray-200' : 'bg-dreibach-card border-white/10'}`;
  const headerClass = `flex flex-col md:flex-row gap-4 justify-between items-center p-6 rounded-sm border ${isLightMode ? 'bg-white border-gray-200' : 'bg-dreibach-card border-white/10'}`;
  const inputClass = `pl-9 rounded-none h-10 w-full ${isLightMode ? 'bg-gray-50 border-gray-300 text-gray-900' : 'bg-black/50 border-white/10 text-white'}`;
  
  const rowClass = `transition-colors border-b ${isLightMode ? 'border-gray-200 hover:bg-gray-50' : 'border-white/10 hover:bg-white/5'}`;
  const headClass = `text-xs uppercase font-bold ${isLightMode ? 'text-gray-700' : 'text-white'}`;
  const cellClass = `text-sm ${isLightMode ? 'text-gray-900' : 'text-white'}`;
  const priceBgClass = isLightMode ? 'bg-green-50 text-green-700' : 'bg-white/5 text-green-400';

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className={headerClass}>
         <form onSubmit={handleSearch} className="flex gap-2 w-full md:w-auto flex-1 max-w-lg">
            <div className="relative w-full">
               <Search className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
               <Input 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search SKU or Title..." 
                  className={inputClass}
               />
            </div>
            <Button type="submit" className="bg-dreibach-teal text-white hover:bg-white hover:text-black rounded-none">
               Search
            </Button>
         </form>
         
         <div className="flex items-center gap-4">
             {activeLead && (
                 <span className="text-xs font-bold uppercase tracking-widest text-green-600 bg-green-100 px-3 py-2 border border-green-200 rounded-sm">
                     Active: {activeLead.company}
                 </span>
             )}
             <Button 
                onClick={handleSync} 
                disabled={syncing}
                variant="outline" 
                className={`rounded-none gap-2 ${isLightMode ? 'border-gray-300 text-gray-600 hover:bg-gray-100' : 'border-white/20 text-gray-400 hover:text-white hover:bg-white/5'}`}
             >
                <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing..." : "Sync Prices"}
             </Button>
         </div>
      </div>

      {/* Results Table */}
      <div className={containerClass}>
         <div className="overflow-x-auto">
            <Table>
               <TableHeader>
                  <TableRow className={rowClass}>
                     <TableHead className="text-dreibach-teal font-bold text-xs uppercase w-24">SKU</TableHead>
                     <TableHead className={`${headClass} min-w-[300px]`}>Product Title</TableHead>
                     <TableHead className="text-gray-400 font-bold text-xs uppercase text-right">Stock</TableHead>
                     <TableHead className="text-gray-400 font-bold text-xs uppercase text-right">LW Cost</TableHead>
                     <TableHead className={`${headClass} text-right ${isLightMode ? 'bg-gray-100' : 'bg-white/5'}`}>500 Qty</TableHead>
                     <TableHead className={`${headClass} text-right ${isLightMode ? 'bg-gray-100' : 'bg-white/5'}`}>1000 Qty</TableHead>
                     <TableHead className="text-gray-400 font-bold text-xs uppercase text-right">Container</TableHead>
                     {/* Truck Column Removed */}
      {/* Draft Mode Banner */}
      {!activeLead && draftProducts?.length > 0 && (
          <div className="bg-dreibach-teal text-white p-4 flex justify-between items-center shadow-lg rounded-sm mb-6">
              <div>
                  <h3 className="font-bold uppercase tracking-widest text-sm">Draft Quote Active</h3>
                  <p className="text-xs opacity-90">{draftProducts.length} Items in Basket</p>
              </div>
              <Button 
                onClick={handleDraftSubmit}
                className="bg-white text-dreibach-teal hover:bg-gray-100 uppercase font-bold text-xs tracking-widest"
              >
                  Proceed to Lead Form
              </Button>
          </div>
      )}
                     <TableHead className="text-dreibach-teal font-bold text-[10px] uppercase text-center w-20">Action</TableHead>
                  </TableRow>
               </TableHeader>
               <TableBody>
                  {products.length > 0 ? (
                      products.map((product) => (
                         <TableRow key={product.sku} className={rowClass}>
                            <TableCell className="font-mono text-dreibach-teal">{product.sku}</TableCell>
                            <TableCell className={`font-medium ${cellClass}`}>{product.title}</TableCell>
                            <TableCell className={`text-right font-mono ${isLightMode ? 'text-gray-600' : 'text-gray-300'}`}>{product.stock_uk || "-"}</TableCell>
                            <TableCell className="text-right">
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 px-2 text-xs text-gray-500 hover:text-dreibach-teal"
                                    onClick={() => toggleCost(product.sku)}
                                >
                                    {revealedCosts[product.sku] ? (
                                        <span className="font-mono font-bold text-dreibach-teal">{product.lw_cost || "N/A"}</span>
                                    ) : (
                                        <div className="flex items-center gap-1">
                                            <Eye size={12} />
                                            <span>View</span>
                                        </div>
                                    )}
                                </Button>
                            </TableCell>
                            <TableCell className={`text-right font-mono font-bold ${priceBgClass}`}>{product.price_500}</TableCell>
                            <TableCell className={`text-right font-mono font-bold ${priceBgClass}`}>{product.price_1000}</TableCell>
                            <TableCell className="text-right font-mono text-gray-400">{product.price_container}</TableCell>
                            {/* Truck Column Removed */}
                            <TableCell className="text-center">
                                <Button 
                                    onClick={() => openAddDialog(product)}
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-8 w-8 p-0 text-gray-500 hover:text-dreibach-teal hover:bg-transparent"
                                >
                                    <PlusCircle size={18} />
                                </Button>
                            </TableCell>
                         </TableRow>
                      ))
                  ) : (
                      <TableRow>
                         <TableCell colSpan={7} className="h-24 text-center text-gray-500">
                            {loading ? "Loading inventory..." : (
                                <div className="flex flex-col items-center gap-2">
                                   <Box size={24} className="opacity-20" />
                                   <span>No products found. Try syncing data.</span>
                                </div>
                            )}
                         </TableCell>
                      </TableRow>
                  )}
               </TableBody>
            </Table>
         </div>
      </div>

      {/* Add Product Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className={`${isLightMode ? 'bg-white text-gray-900' : 'bg-dreibach-card border-white/10 text-white'}`}>
            <DialogHeader>
                <DialogTitle>Add to Lead: {activeLead?.company}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
                <div className={`p-3 rounded text-sm mb-4 ${isLightMode ? 'bg-gray-100 text-gray-900' : 'bg-white/5 text-white'}`}>
                    <p className="font-bold">{selectedProduct?.sku}</p>
                    <p>{selectedProduct?.title}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase font-bold text-gray-500">Quantity</Label>
                        <Input 
                            value={quantity} 
                            onChange={(e) => setQuantity(e.target.value)} 
                            className={`mt-1 ${isLightMode ? 'bg-white border-gray-300' : 'bg-black/50 border-white/10'}`} 
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase font-bold text-gray-500">Special Price (£)</Label>
                        <Input 
                            value={customPrice} 
                            onChange={(e) => setCustomPrice(e.target.value)} 
                            className={`mt-1 ${isLightMode ? 'bg-white border-gray-300' : 'bg-black/50 border-white/10'}`} 
                        />
                    </div>
                </div>
            </div>
            <DialogFooter>
                <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleConfirmAdd} className="bg-dreibach-teal text-white">Confirm Add</Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Draft Review Dialog - REMOVED */}
      {/* 
      <Dialog open={showDraftReview} onOpenChange={setShowDraftReview}>
        <DialogContent className={`max-w-3xl ${isLightMode ? 'bg-white text-gray-900' : 'bg-dreibach-card border-white/10 text-white'}`}>
            <DialogHeader>
                <DialogTitle className="uppercase tracking-widest">Finalize Quote & Create Lead</DialogTitle>
            </DialogHeader>
            
            <div className="grid md:grid-cols-2 gap-8 py-4">
                <div className="border-r border-gray-200 dark:border-white/10 pr-4">
                    <h4 className="text-xs font-bold uppercase text-gray-500 mb-4">Quote Items</h4>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {draftProducts?.map((item, idx) => (
                            <div key={idx} className={`p-2 text-xs flex justify-between items-center rounded ${isLightMode ? 'bg-gray-50' : 'bg-white/5'}`}>
                                <div>
                                    <span className="font-bold block">{item.sku}</span>
                                    <span className="opacity-70">{item.title}</span>
                                </div>
                                <div className="text-right">
                                    <span className="font-bold block">£{item.special_price || item.price_point}</span>
                                    <span className="opacity-70">x{item.quantity}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-4">
                    <h4 className="text-xs font-bold uppercase text-gray-500 mb-4">Customer Details</h4>
                    <div>
                        <Label className="text-[10px] uppercase">Company Name</Label>
                        <Input 
                            value={draftLeadDetails.company}
                            onChange={(e) => setDraftLeadDetails({...draftLeadDetails, company: e.target.value})}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase">Contact Name</Label>
                        <Input 
                            value={draftLeadDetails.name}
                            onChange={(e) => setDraftLeadDetails({...draftLeadDetails, name: e.target.value})}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase">Email</Label>
                        <Input 
                            value={draftLeadDetails.email}
                            onChange={(e) => setDraftLeadDetails({...draftLeadDetails, email: e.target.value})}
                            className={inputClass}
                        />
                    </div>
                </div>
            </div>

            <DialogFooter>
                <Button variant="ghost" onClick={() => setShowDraftReview(false)}>Back</Button>
                <Button onClick={handleDraftSubmit} className="bg-dreibach-teal text-white">Create Lead</Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
};

export default ProductPricing;
